import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Heart, Lock, Clock, Users, Sun, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useSite, setSeo } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr, localDay, mediaUrl, TYPE_META } from "@/lib/api";
import { Prose } from "@/components/common";
import { PairsList, PairsReader } from "@/components/ParentChild";
import { VideoBlock, AudioBlock, LinksBlock } from "@/components/Media";
import { ResourceGrid } from "@/components/ResourceCard";
import { GrowthListBlock } from "@/components/EmailCapture";
import { CommunityPromo } from "@/components/Promos";

const Meta = ({ icon: I, children, testid }) => (
  <span className="inline-flex items-center gap-2 rounded-full bg-bt-sage px-4 py-2 text-sm text-bt-heading" data-testid={testid}><I size={15} /> {children}</span>
);

function Items({ r }) {
  if (!r.items.length) return null;
  if (r.type === "prompt")
    return (
      <ol className="space-y-4" data-testid="prompt-list">
        {r.items.map((t, i) => (
          <li key={i} className="card-bt flex gap-5 p-6 sm:p-8">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bt-primary font-heading text-bt-bg">{i + 1}</span>
            <p className="font-heading text-2xl leading-snug text-bt-heading">{t}</p>
          </li>
        ))}
      </ol>
    );
  return (
    <ul className="divide-y divide-[color:var(--bt-border)] rounded-[28px] bg-bt-sage px-6 sm:px-10" data-testid="affirmation-list">
      {r.items.map((t, i) => <li key={i} className="py-6 font-heading text-2xl leading-snug text-bt-heading sm:text-3xl">{t}</li>)}
    </ul>
  );
}

const Steps = ({ steps }) =>
  steps.length ? (
    <ol className="space-y-3" data-testid="steps-list">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-4 rounded-[22px] border border-bt-border bg-bt-surface p-5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-bt-sage font-semibold text-bt-primary">{i + 1}</span>
          <p className="pt-1 text-lg leading-relaxed text-bt-heading">{s}</p>
        </li>
      ))}
    </ol>
  ) : null;

function LockedPanel() {
  const { user } = useAuth();
  return (
    <div className="relative -mt-24 rounded-[32px] bg-bt-primary p-8 pt-32 text-center text-bt-bg sm:p-12 sm:pt-32" style={{ background: "linear-gradient(180deg, transparent 0%, var(--bt-primary) 35%)" }} data-testid="locked-panel">
      <Lock className="mx-auto mb-4" />
      <h3 className="text-3xl" style={{ color: "var(--bt-background)" }}>This one's for members</h3>
      <p className="mx-auto mt-3 max-w-md opacity-90">Unlock the full script, plus the whole library, your Growth Streak and favourites.</p>
      <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
        <Link to="/membership" className="inline-flex min-h-[52px] items-center justify-center rounded-full bg-bt-bg px-7 font-semibold text-bt-primary" data-testid="locked-membership-cta">See membership</Link>
        {!user && <Link to={`/login?next=${encodeURIComponent(window.location.pathname)}`} className="inline-flex min-h-[52px] items-center justify-center rounded-full border-2 border-white/40 px-7 font-semibold" data-testid="locked-login-link">I'm already a member</Link>}
      </div>
    </div>
  );
}

function Actions({ r, onChange }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const today = localDay();
  const [busy, setBusy] = useState(false);
  const doneToday = (r.completed_dates || []).includes(today);
  const guard = () => {
    if (!user) {
      toast("Create a free account to track your growth", { action: { label: "Sign up", onClick: () => nav("/register") } });
      return false;
    }
    return true;
  };
  const handleErr = (e) => {
    if (e.response?.status === 403) toast(formatErr(e), { action: { label: "Membership", onClick: () => nav("/membership") } });
    else toast.error(formatErr(e));
  };
  const complete = async () => {
    if (busy || !guard()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/me/complete/${r.id}`, { date: today });
      onChange({ completed_dates: [...(r.completed_dates || []), today] });
      toast.success(`Growth day logged. Current streak: ${data.current} day${data.current === 1 ? "" : "s"}.`);
    } catch (e) {
      handleErr(e);
    }
    setBusy(false);
  };
  const fav = async () => {
    if (busy || !guard()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/me/favourite/${r.id}`);
      onChange({ is_favourite: data.is_favourite });
      toast.success(data.is_favourite ? "Saved to favourites" : "Removed from favourites");
    } catch (e) {
      handleErr(e);
    }
    setBusy(false);
  };
  return (
    <div className="flex flex-col gap-3 sm:flex-row" data-testid="resource-actions">
      {r.completable && (
        <button onClick={complete} disabled={doneToday || busy} className="btn-primary flex-1" data-testid="mark-complete-button">
          <Check size={18} /> {doneToday ? "Completed today" : "Mark as complete"}
        </button>
      )}
      <button onClick={fav} className="btn-secondary" data-testid="favourite-toggle-button">
        <Heart size={18} fill={r.is_favourite ? "currentColor" : "none"} /> {r.is_favourite ? "Saved" : "Save"}
      </button>
    </div>
  );
}

export default function Resource() {
  const { slug } = useParams();
  const { settings } = useSite();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("guided");

  const load = useCallback(() => {
    api.get(`/resources/${slug}`).then((res) => {
      setR(res.data);
      setSeo(res.data.seo_title || `${res.data.title} | ${settings.brand.site_name}`, res.data.seo_description || res.data.description);
    }).catch((e) => setErr(formatErr(e)));
  }, [slug, settings.brand.site_name]);
  useEffect(() => {
    load();
    return () => setSeo(settings.seo.title, settings.seo.description);
  }, [load, user, settings.seo]);

  if (err) return <div className="container-bt py-24 text-center" data-testid="resource-error"><h1 className="text-4xl">Resource not found</h1><Link to="/library" className="btn-primary mt-8">Back to library</Link></div>;
  if (!r) return <div className="min-h-[60vh]" />;
  const area = settings.library_areas.find((a) => a.slug === r.area);
  const patch = (p) => setR({ ...r, ...p });

  return (
    <article className="container-bt py-10 sm:py-14" data-testid="resource-page">
      <Link to={`/library${area ? `?area=${area.slug}` : ""}`} className="btn-ghost -ml-4 mb-6" data-testid="resource-back-link"><ArrowLeft size={18} /> {area ? area.title : "Library"}</Link>
      <div className="mx-auto max-w-3xl">
        <p className="eyebrow mb-4" data-testid="resource-type">{TYPE_META[r.type] || r.type}{r.category && ` · ${r.category}`}</p>
        <h1 className="text-4xl leading-tight sm:text-5xl" data-testid="resource-title">{r.title}</h1>
        {r.description && <p className="mt-5 text-lg leading-relaxed">{r.description}</p>}
        <div className="mt-6 flex flex-wrap gap-2">
          {r.age && <Meta icon={Users} testid="resource-age">Ages {r.age}</Meta>}
          {r.suggested_time && <Meta icon={Sun} testid="resource-suggested-time">{r.suggested_time}</Meta>}
          {r.duration && <Meta icon={Clock}>{r.duration}</Meta>}
          <Meta icon={r.access === "free" ? Sparkles : Lock} testid="resource-access">{r.access === "free" ? "Free" : "Members"}</Meta>
        </div>
        {r.image && <img src={mediaUrl(r.image)} alt="" className="mt-10 aspect-[16/9] w-full rounded-[32px] object-cover" />}

        <div className="mt-10 space-y-8">
          <VideoBlock url={r.video_url} />
          <AudioBlock url={r.audio_url} label={r.type === "parent_child" ? "Play together: parent & child voices" : "Listen"} />
          {r.body && <Prose text={r.body} />}
          {r.pairs.length > 0 && (
            <div>
              {!r.locked && r.pairs.length > 1 && (
                <div className="mb-5 inline-flex rounded-full bg-bt-sage p-1" data-testid="pairs-mode-toggle">
                  {[["guided", "Together mode"], ["all", "See all"]].map(([k, l]) => (
                    <button key={k} onClick={() => setMode(k)} className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${mode === k ? "bg-bt-primary text-bt-bg" : "text-bt-primary"}`} data-testid={`pairs-mode-${k}`}>{l}</button>
                  ))}
                </div>
              )}
              {mode === "guided" && !r.locked && r.pairs.length > 1 ? <PairsReader pairs={r.pairs} /> : <PairsList pairs={r.pairs} />}
            </div>
          )}
          <Items r={r} />
          <Steps steps={r.steps} />
          {r.locked && <LockedPanel />}
          <LinksBlock downloads={r.downloads} links={r.links} />
          {!r.locked && <Actions r={r} onChange={patch} />}
        </div>
      </div>

      <div className="mx-auto mt-20 max-w-5xl space-y-16">
        {!user ? <GrowthListBlock source={`resource:${r.slug}`} /> : settings.community.show_after_content && <CommunityPromo compact />}
        {r.related?.length > 0 && (
          <section data-testid="related-resources">
            <h2 className="mb-8 text-3xl">You might also like</h2>
            <ResourceGrid items={r.related} />
          </section>
        )}
      </div>
    </article>
  );
}
